/**
 * The name rules of campaigns, contact lists and survey question sets — plan-015 NAM, gap G18, owner answer 7
 * (decision D-13), design §28.5.10, contract IC-DG-10.
 *
 * ONE MODULE, THREE READERS. The API refuses a name with these messages, the console checks the name with the same
 * functions as it is typed, and migration 0119 back-fills `name_key` with a SQL expression that mirrors
 * {@link nameKeyOf} exactly (its pg test compares the two on Latin, Greek and mixed names). A rule changed here and
 * not there would let the console say "Looks good." to a name the API refuses.
 *
 * THE DECIDED RULE (answer 7). The design's limits and character set; names are unique case-insensitively within a
 * workspace — campaigns among campaigns, lists among lists; names already stored that break the rule are kept as they
 * are until renamed. Uniqueness is the database's: `(account_id, workspace_id, name_key)` is a unique index, and a
 * violation is `409 name_taken` with the design's sentence.
 *
 * THE TWO CHARACTER SETS ARE THE DESIGN'S (`design/WeTalk.dc.html`, the export of 2026-10-09), message for message:
 *
 *   · campaign (and question set) — 3 to 60 characters; letters in any language, numbers, spaces and - _ . , ' & ( ).
 *   · contact list — at most 40 characters (the field's `maxlength` and its "0/40" counter); letters, numbers,
 *     spaces, hyphens and underscores; starting with a letter, ending with a letter or number, one separator at a
 *     time, at least 3 characters and at least two letters. The design's New contact list panel draws exactly these
 *     checks, and the prototype outranks design §28's shorthand `{1,40}` over the campaign set (CLAUDE.md §2's
 *     authority order) — recorded as a deviation in NAM's report.
 *
 * Lengths count code points (what the `u` regular expressions count), on the name as stored: the API trims a name
 * before it is checked and stored.
 */
/** The fewest characters a campaign (and a question set) name may have: "At least 3 characters." */
export const CAMPAIGN_NAME_MINIMUM = 3;
/** The most characters a campaign (and a question set) name may have: the field's "0/60". */
export const CAMPAIGN_NAME_MAXIMUM = 60;
/** The fewest characters a contact list's name may have: "At least 3 characters." */
export const CONTACT_LIST_NAME_MINIMUM = 3;
/** The most characters a contact list's name may have: the field's "0/40". */
export const CONTACT_LIST_NAME_MAXIMUM = 40;
/** A whole campaign name the rule accepts (design §28.5.10). */
export const CAMPAIGN_NAME = /^[\p{L}\p{N} \-_.,'&()]{3,60}$/u;
/** A question set's name follows the campaign rule (IC-DG-10). */
export const QUESTION_SET_NAME = CAMPAIGN_NAME;
/**
 * A contact list name's characters and length. The design's structure checks (first and last character, one
 * separator at a time, two letters) are in {@link contactListNameProblem}; a name passes the rule only when that
 * returns `null`.
 */
export const CONTACT_LIST_NAME = /^[\p{L}\p{N} _-]{3,40}$/u;
/** The design's sentence for a campaign name already used in the workspace (`409 name_taken`). */
export const CAMPAIGN_NAME_TAKEN_MESSAGE = "You already have a campaign with this name.";
/** The design's sentence for a list name already used in the workspace (`409 name_taken`). */
export const CONTACT_LIST_NAME_TAKEN_MESSAGE = "You already have a list with this name. Names are not case-sensitive.";
/** The question-set wording of the same refusal (design §28.4.4: "and the list/set wording"). */
export const QUESTION_SET_NAME_TAKEN_MESSAGE = "You already have a question set with this name.";
/** The design's "At least 3 characters." — the same words for every kind of name. */
export const NAME_TOO_SHORT_MESSAGE = "At least 3 characters.";
/** Postgres' unique indexes that back the rule (migration 0119), named so the API maps exactly these to `name_taken`. */
export const CAMPAIGN_NAME_KEY_UNIQUE = "campaign_name_key_unique";
export const CONTACT_LIST_NAME_KEY_UNIQUE = "contact_list_name_key_unique";
/**
 * The key a name is unique by: Unicode NFC, trimmed, every run of white space one space, lower-cased by the root
 * locale, and the final sigma folded into σ (so "ΟΔΟΣ", "Οδος" and "οδοσ" are one name). Migration 0119's back-fill
 * mirrors this expression in SQL.
 */
export function nameKeyOf(name) {
    return name.normalize("NFC").trim().replace(/\s+/gu, " ").toLocaleLowerCase("und").replace(/ς/gu, "σ");
}
/** Whether `name` is already one of `existing`, by {@link nameKeyOf}. */
export function isNameTaken(name, existing) {
    const wanted = nameKeyOf(name);
    for (const one of existing) {
        if (nameKeyOf(one) === wanted)
            return true;
    }
    return false;
}
function lengthOf(text) {
    return [...text].length;
}
/**
 * What is wrong with a campaign name, in the design's words, or `null` when the rule accepts it. The characters are
 * checked as typed and the length on the trimmed name, as the design's New campaign does. An empty name has its own
 * sentence for the API; the console shows nothing while the field is empty.
 */
export function campaignNameProblem(name) {
    const value = name.trim();
    if (value === "")
        return "Give the campaign a name.";
    const bad = /[^\p{L}\p{N} \-_.,'&()]/u.exec(name);
    if (bad !== null)
        return `"${bad[0]}" can't be used. Letters, numbers, spaces and - _ . , ' & ( ) only.`;
    const length = lengthOf(value);
    if (length < CAMPAIGN_NAME_MINIMUM)
        return NAME_TOO_SHORT_MESSAGE;
    if (length > CAMPAIGN_NAME_MAXIMUM)
        return `At most ${String(CAMPAIGN_NAME_MAXIMUM)} characters.`;
    return null;
}
/** A question set's name: the campaign rule (IC-DG-10). */
export function questionSetNameProblem(name) {
    const problem = campaignNameProblem(name);
    return problem === "Give the campaign a name." ? "Give the question set a name." : problem;
}
/**
 * What is wrong with a contact list's name, in the design's words and order, or `null` when the rule accepts it.
 * Checked on the name as given: the design checks the field as typed (so "   " is "Start with a letter."), and the
 * API passes the trimmed name (so an all-space name reaches it as "", "Give the list a name.").
 */
export function contactListNameProblem(name) {
    if (name === "")
        return "Give the list a name.";
    const bad = /[^\p{L}\p{N} _-]/u.exec(name);
    if (bad !== null)
        return `"${bad[0]}" can't be used. Only letters, numbers, spaces, hyphens (-) and underscores (_).`;
    if (!/^\p{L}/u.test(name))
        return "Start with a letter.";
    if (/[ _-]$/u.test(name))
        return "End with a letter or number.";
    if (/[ _-]{2,}/u.test(name))
        return "Use one space, hyphen or underscore at a time.";
    const length = lengthOf(name);
    if (length < CONTACT_LIST_NAME_MINIMUM)
        return NAME_TOO_SHORT_MESSAGE;
    if ((name.match(/\p{L}/gu) ?? []).length < 2)
        return "Use at least two letters so it is easy to recognise.";
    if (length > CONTACT_LIST_NAME_MAXIMUM)
        return `At most ${String(CONTACT_LIST_NAME_MAXIMUM)} characters.`;
    return null;
}
//# sourceMappingURL=name-rule.js.map